// Frontend team management service following auth-service patterns
// Handles team member management, invitations, and role assignments

import { authService } from './auth-service';

// Type definitions for team management
export interface TeamMember {
  userId: string;          // Primary Key
  email: string;          // GSI
  status: 'active' | 'pending' | 'suspended' | 'inactive';
  role: string;           // 'admin', 'user', 'viewer'
  permissions: string[];   // Granular permissions
  isSystemUser?: boolean;  // Undeletable system Super Admin (break-glass)
  displayName?: string;    // From S3 profile
  photoUrl?: string;       // Signed S3 URL for profile photo
  procurementActivityIds?: string[]; // Assigned fund/grant/scholarship KB ids (admins only)
  invitedBy?: string;     // userId who invited
  invitedAt?: string;
  activatedAt?: string;
  lastLoginAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface TeamInvitation {
  invitationId: string;    // Primary Key (UUID)
  email: string;          // GSI
  role: string;
  permissions: string[];
  invitedBy: string;
  status: 'pending' | 'accepted' | 'expired' | 'cancelled';
  expiresAt: string;      // TTL for auto-cleanup
  createdAt: string;
  acceptedAt?: string;
}

export interface RoleDefinition {
  role: string;
  permissions: string[];
  description: string;
}

export interface InviteUserRequest {
  email: string;
  role: string;
  permissions: string[];
  procurementActivityIds?: string[];
}

export interface UpdateMemberRoleRequest {
  role: string;
  permissions: string[];
}

/**
 * Team Management Service for handling team operations
 * Follows the established auth-service pattern with secure cookie authentication
 * Handles member management, invitations, and role assignments
 */
class TeamManagementService {
  private readonly API_BASE_URL = ''; // Use relative URLs - nginx will proxy to backend

  /**
   * Get all team members for the current organization
   * Returns list of active and inactive team members
   */
  async getTeamMembers(): Promise<TeamMember[]> {
    try {
      const members = await authService.makeAuthenticatedRequest<TeamMember[]>('/api/team/members');
      return members;
    } catch (error) {
      console.error('Failed to fetch team members:', error);
      throw error;
    }
  }

  /**
   * Invite a new user to the team
   * Sends invitation email and creates pending invitation record
   */
  async inviteUser(email: string, role: string, permissions: string[], procurementActivityIds?: string[]): Promise<TeamInvitation> {
    try {
      const request: InviteUserRequest = {
        email: email.toLowerCase().trim(),
        role,
        permissions,
      };
      if (procurementActivityIds && procurementActivityIds.length > 0) {
        request.procurementActivityIds = procurementActivityIds;
      }

      const invitation = await authService.makeAuthenticatedRequest<TeamInvitation>('/api/team/invitations', {
        method: 'POST',
        body: JSON.stringify(request),
      });

      return invitation;
    } catch (error) {
      console.error('Failed to invite user:', error);
      throw error;
    }
  }

  /**
   * Get all pending invitations
   * Returns list of invitations that haven't been accepted yet
   */
  async getPendingInvitations(): Promise<TeamInvitation[]> {
    try {
      const invitations = await authService.makeAuthenticatedRequest<TeamInvitation[]>('/api/team/invitations');
      return invitations;
    } catch (error) {
      console.error('Failed to fetch pending invitations:', error);
      throw error;
    }
  }

  /**
   * Update a team member's role and permissions
   * Requires admin privileges to execute
   */
  async updateMemberRole(userId: string, role: string, permissions: string[]): Promise<TeamMember> {
    try {
      const request: UpdateMemberRoleRequest = {
        role,
        permissions,
      };

      const updatedMember = await authService.makeAuthenticatedRequest<TeamMember>(`/api/team/members/${userId}/role`, {
        method: 'PUT',
        body: JSON.stringify(request),
      });

      return updatedMember;
    } catch (error) {
      console.error('Failed to update member role:', error);
      throw error;
    }
  }

  /**
   * Permanently delete a team member's Cognito account (irreversible). The backend rejects
   * deletion of the system Super Admin. This is NOT a reversible "deactivate".
   */
  async removeMember(userId: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await authService.makeAuthenticatedRequest<{ success: boolean; message: string }>(`/api/team/members/${userId}`, {
        method: 'DELETE',
      });

      return response;
    } catch (error) {
      console.error('Failed to remove team member:', error);
      throw error;
    }
  }

  /**
   * Cancel a pending invitation
   * Marks invitation as cancelled and prevents acceptance
   */
  async cancelInvitation(invitationId: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await authService.makeAuthenticatedRequest<{ success: boolean; message: string }>(`/api/team/invitations/${invitationId}`, {
        method: 'DELETE',
      });

      return response;
    } catch (error) {
      console.error('Failed to cancel invitation:', error);
      throw error;
    }
  }

  /**
   * Resend an invitation email
   * Updates expiration date and sends new invitation email
   */
  async resendInvitation(invitationId: string): Promise<TeamInvitation> {
    try {
      const invitation = await authService.makeAuthenticatedRequest<TeamInvitation>(`/api/team/invitations/${invitationId}/resend`, {
        method: 'POST',
      });

      return invitation;
    } catch (error) {
      console.error('Failed to resend invitation:', error);
      throw error;
    }
  }

  /**
   * Accept an invitation (called by invited user)
   * Activates the user's membership and sets up their account
   */
  async acceptInvitation(invitationId: string, token: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await authService.makeAuthenticatedRequest<{ success: boolean; message: string }>(`/api/team/invitations/${invitationId}/accept`, {
        method: 'POST',
        body: JSON.stringify({ token }),
      });

      return response;
    } catch (error) {
      console.error('Failed to accept invitation:', error);
      throw error;
    }
  }

  /**
   * Get available roles and their permissions
   * Returns the role definitions for the organization
   */
  async getAvailableRoles(): Promise<{ role: string; permissions: string[]; description: string }[]> {
    try {
      const roles = await authService.makeAuthenticatedRequest<{ role: string; permissions: string[]; description: string }[]>('/api/team/roles');
      return roles;
    } catch (error) {
      console.error('Failed to fetch available roles:', error);
      // Return default roles as fallback
      return [
        {
          role: 'admin',
          permissions: ['read', 'write', 'admin', 'team:manage', 'team:invite'],
          description: 'Full access to all features and team management',
        },
        {
          role: 'user',
          permissions: ['read', 'write'],
          description: 'Standard user with read and write access',
        },
        {
          role: 'viewer',
          permissions: ['read'],
          description: 'Read-only access to content',
        },
      ];
    }
  }

  /**
   * Get all user procurement activity assignments (batch)
   * Returns a mapping of userId → procurement activity kbIds
   */
  async getAllProcurementActivities(): Promise<Record<string, string[]>> {
    try {
      return await authService.makeAuthenticatedRequest<Record<string, string[]>>('/api/team/procurement-activities');
    } catch (error) {
      console.error('Failed to fetch procurement activity assignments:', error);
      return {};
    }
  }

  /**
   * Get procurement activity assignments for a specific user
   */
  async getUserProcurementActivities(userId: string): Promise<string[]> {
    try {
      const response = await authService.makeAuthenticatedRequest<{ procurementActivityIds: string[] }>(
        `/api/team/members/${userId}/procurement-activities`,
      );
      return response.procurementActivityIds;
    } catch (error) {
      console.error('Failed to fetch user procurement activities:', error);
      return [];
    }
  }

  /**
   * Update procurement activity assignments for a user
   */
  async updateUserProcurementActivities(userId: string, procurementActivityIds: string[]): Promise<void> {
    try {
      await authService.makeAuthenticatedRequest<{ procurementActivityIds: string[] }>(
        `/api/team/members/${userId}/procurement-activities`,
        {
          method: 'PUT',
          body: JSON.stringify({ procurementActivityIds }),
        },
      );
    } catch (error) {
      console.error('Failed to update user procurement activities:', error);
      throw error;
    }
  }
}

// Export singleton instance following auth-service pattern
export const teamManagementService = new TeamManagementService();
